import React, { useEffect, useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const copyByLanguage = {
  es: {
    mobileHint: "Intentamos reproducir con audio. Si tu navegador lo bloquea, toca el video para escucharlo.",
    desktopHint: "Mira cómo Voxara transcribe, detecta momentos clave y abre el chat contextual.",
    cta: "Ver video",
    modalTitle: "Video de Voxara",
    modalDescription: "Puedes cerrarlo en cualquier momento si no quieres seguir viéndolo.",
  },
  en: {
    mobileHint: "We try to autoplay with sound. If the browser blocks it, tap the video to hear it.",
    desktopHint: "See how Voxara transcribes, surfaces key moments, and opens contextual chat.",
    cta: "Watch video",
    modalTitle: "Voxara video",
    modalDescription: "You can close it any time if you don’t want to keep watching.",
  },
};

export const LandingPromoVideoCard = ({ language = "es", videoSrc }) => {
  const [audioFallback, setAudioFallback] = useState(false);
  const mobileVideoRef = useRef(null);
  const copy = copyByLanguage[language] || copyByLanguage.es;

  const primeVideoFrame = (video, frameTime = 1.2) => {
    if (!video) return;
    const applyFrame = () => {
      if (Number.isFinite(video.duration) && video.duration > frameTime) {
        video.currentTime = frameTime;
      }
    };

    if (video.readyState >= 1) {
      applyFrame();
      return;
    }

    video.addEventListener("loadedmetadata", applyFrame, { once: true });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const video = mobileVideoRef.current;
    if (!video) return;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let isDisposed = false;

    const syncPlaybackForViewport = async () => {
      primeVideoFrame(video, 0.8);

      if (!mediaQuery.matches) {
        video.pause();
        video.muted = true;
        if (!isDisposed) {
          setAudioFallback(false);
        }
        return;
      }

      try {
        video.muted = false;
        await video.play();
        if (!isDisposed) {
          setAudioFallback(false);
        }
      } catch (error) {
        if (isDisposed) return;
        setAudioFallback(true);
        video.muted = true;
        await video.play().catch(() => {});
      }
    };

    syncPlaybackForViewport();

    const handleViewportChange = () => {
      syncPlaybackForViewport();
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleViewportChange);
    } else {
      mediaQuery.addListener(handleViewportChange);
    }

    return () => {
      isDisposed = true;
      video.pause();
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleViewportChange);
      } else {
        mediaQuery.removeListener(handleViewportChange);
      }
    };
  }, []);

  return (
    <div className="w-full">
      <Card className="border-border bg-card p-4 card-hover" data-testid="landing-mobile-video-card">
        <video
          ref={mobileVideoRef}
          className="aspect-video w-full rounded-2xl border border-border/60 bg-black object-cover"
          src={videoSrc}
          controls
          loop
          playsInline
          preload="metadata"
          data-testid="landing-mobile-promo-video"
        />

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-testid="landing-mobile-video-description">
          {audioFallback ? copy.mobileHint : copy.desktopHint}
        </p>

        {audioFallback ? (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs text-primary" data-testid="landing-mobile-video-audio-hint">
            <Volume2 className="h-3.5 w-3.5" />
            {language === "es" ? "Toca el video para activar el audio" : "Tap the video to enable audio"}
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export const LandingPromoVideoDialog = ({ language = "es", videoSrc, open, onOpenChange }) => {
  const modalVideoRef = useRef(null);
  const copy = copyByLanguage[language] || copyByLanguage.es;

  useEffect(() => {
    const modalVideo = modalVideoRef.current;
    if (!modalVideo) return;

    if (open) {
      modalVideo.currentTime = 0;
      modalVideo.muted = false;
      modalVideo.play().catch(() => {});
      return;
    }

    modalVideo.pause();
    modalVideo.currentTime = 0;
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl overflow-hidden border-border bg-card p-0"
        closeButtonTestId="landing-video-modal-close-button"
        data-testid="landing-video-modal"
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle data-testid="landing-video-modal-title">{copy.modalTitle}</DialogTitle>
          <DialogDescription data-testid="landing-video-modal-description">{copy.modalDescription}</DialogDescription>
        </DialogHeader>

        <div className="px-4 pb-4 sm:px-6 sm:pb-6">
          <video
            ref={modalVideoRef}
            className="aspect-video w-full rounded-2xl bg-black object-contain"
            src={videoSrc}
            controls
            playsInline
            preload="metadata"
            data-testid="landing-video-modal-player"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
